import * as React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@shared';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';

export interface DaySheetlySelectOption<V extends string = string> {
  value: V;
  label: string;
  disabled?: boolean;
}

interface CommonProps {
  label?: string;
  helperText?: string;
  error?: string;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  testId?: string;
  className?: string;
}

type SingleProps<V extends string> = {
  mode?: 'single';
  value: V | null;
  onValueChange: (value: V | null) => void;
  options: readonly DaySheetlySelectOption<V>[];
};

type MultipleProps<V extends string> = {
  mode: 'multiple';
  value: V[];
  onValueChange: (value: V[]) => void;
  options: readonly DaySheetlySelectOption<V>[];
};

export type DaySheetlySelectProps<V extends string = string> = (
  | SingleProps<V>
  | MultipleProps<V>
) &
  CommonProps;

/**
 * DaySheetlySelect is a single API for both single- and multi-select pickers.
 *
 * - `mode="single"` delegates to Radix `Select` (keyboard-friendly, accessible).
 * - `mode="multiple"` renders a `Popover` + checkbox list (the same pattern
 *   used by the `MemberFilter` in `ReportFilters`) and reports an array of
 *   values back to the caller.
 *
 * Before reaching for `Select` / `Popover` directly, search the codebase for
 * `DaySheetlySelect` — this component is the canonical wrapper.
 */
function DaySheetlySelect<V extends string = string>(
  props: DaySheetlySelectProps<V>,
) {
  const {
    label,
    helperText,
    error,
    placeholder = 'Select...',
    emptyMessage = 'No options',
    disabled,
    testId,
    className,
  } = props;

  const generatedId = React.useId();
  const fieldId = `${testId ?? 'daysheetly-select'}-field`;
  const helperId = helperText || error ? `${fieldId}-helper` : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <Label
          htmlFor={fieldId}
          className={cn(disabled && 'opacity-70')}
        >
          {label}
        </Label>
      )}
      <div id={fieldId} aria-describedby={helperId} aria-invalid={error ? 'true' : 'false'}>
        {props.mode === 'multiple' ? (
          <MultipleSelect {...props} placeholder={placeholder} emptyMessage={emptyMessage} disabled={disabled} testId={testId} error={error} />
        ) : (
          <SingleSelect {...props} placeholder={placeholder} disabled={disabled} testId={testId} error={error} />
        )}
      </div>
      {(helperText || error) && (
        <p
          id={helperId}
          className={cn(
            'text-xs',
            error ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {error ?? helperText}
        </p>
      )}
    </div>
  );
}

function SingleSelect<V extends string>({
  options,
  value,
  onValueChange,
  placeholder,
  disabled,
  testId,
  error,
}: SingleProps<V> & {
  placeholder: string;
  disabled?: boolean;
  testId?: string;
  error?: string;
}) {
  const selectedOption = options.find((o) => o.value === value);

  return (
    <Select
      value={value ?? undefined}
      onValueChange={(next) => onValueChange(next as V)}
      disabled={disabled}
    >
      <SelectTrigger
        data-testid={testId ? `${testId}-trigger` : undefined}
        className={cn(error && 'border-destructive')}
        aria-invalid={error ? 'true' : 'false'}
      >
        <SelectValue placeholder={placeholder}>
          {selectedOption?.label ?? placeholder}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">
            No options
          </div>
        ) : (
          options.map((opt) => (
            <SelectItem
              key={opt.value}
              value={opt.value}
              disabled={opt.disabled}
              data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
            >
              {opt.label}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}

function MultipleSelect<V extends string>({
  options,
  value,
  onValueChange,
  placeholder,
  emptyMessage,
  disabled,
  testId,
  error,
}: MultipleProps<V> & {
  placeholder: string;
  emptyMessage: string;
  disabled?: boolean;
  testId?: string;
  error?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const selectedSet = React.useMemo(() => new Set(value), [value]);
  const total = options.length;
  const selectedCount = value.length;
  const allSelected = total > 0 && selectedCount === total;

  const toggle = (optionValue: V) => {
    const next = new Set(selectedSet);
    if (next.has(optionValue)) {
      next.delete(optionValue);
    } else {
      next.add(optionValue);
    }
    onValueChange(options.filter((o) => next.has(o.value)).map((o) => o.value));
  };

  const selectAll = () => onValueChange(options.filter((o) => !o.disabled).map((o) => o.value));
  const clear = () => onValueChange([]);

  const triggerLabel = allSelected
    ? 'All'
    : selectedCount === 0
      ? placeholder
      : `${selectedCount} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId ? `${testId}-trigger` : undefined}
          className={cn(
            'w-full justify-between font-normal',
            !value.length && 'text-muted-foreground',
            error && 'border-destructive',
          )}
        >
          <span>{triggerLabel}</span>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        data-testid={testId ? `${testId}-content` : undefined}
      >
        <div className="max-h-64 space-y-1 overflow-y-auto p-2">
          {options.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          ) : (
            options.map((opt) => {
              const checked = selectedSet.has(opt.value);
              return (
                <label
                  key={opt.value}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent',
                    opt.disabled && 'cursor-not-allowed opacity-50',
                  )}
                  data-testid={
                    testId ? `${testId}-option-${opt.value}` : undefined
                  }
                >
                  <Checkbox
                    checked={checked}
                    disabled={opt.disabled}
                    onCheckedChange={() => !opt.disabled && toggle(opt.value)}
                  />
                  <span className="flex-1 truncate">{opt.label}</span>
                  {checked && <Check className="h-4 w-4 text-primary" />}
                </label>
              );
            })
          )}
        </div>
        {options.length > 0 && (
          <div className="flex items-center justify-end gap-2 border-t px-2 py-1.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={selectAll}
              disabled={disabled || allSelected}
              data-testid={testId ? `${testId}-select-all` : undefined}
            >
              Select all
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={clear}
              disabled={disabled || selectedCount === 0}
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X className="mr-1 h-3 w-3" />
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

DaySheetlySelect.displayName = 'DaySheetlySelect';

export { DaySheetlySelect };
